import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import IWGeneralBadge from 'shared/components/thunderbolt/IWGeneralBadge'
import IWGenericTable, {
  GenericTableColumn,
} from 'shared/components/thunderbolt/IWGenericTable'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import LayoutContext from 'shared/contexts/LayoutContext'
import {
  CertficateInfo,
  getLsAdminCertificateInfo,
} from 'shared/loadSchedulingClient'
import styled from 'styled-components'
import LsAdminPageSectionHeader from './LsAdminPageSectionHeader'
import { lsAdminCertificateInformationTableSchema } from './tableSchemas'

const USE_QUERY_KEY = 'ls-admin-certs-info'

const tableColumns = lsAdminCertificateInformationTableSchema

const StyledBadgeDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

type TableItem = Omit<
  CertficateInfo,
  | 'org'
  | 'subject'
  | 'zone'
  | 'currentTime'
  | 'expired'
  | 'expiringWithinAMonth'
  | 'notAfter'
> & {
  notAfter: string
  expired: JSX.Element
}

export default function LsAdminCertificateInfos() {
  const { t } = useTranslation()
  const { timezone } = useContext(LayoutContext)
  const [items, setItems] = useState<TableItem[]>([])

  const { data: certs, isLoading } = useQuery({
    queryKey: [USE_QUERY_KEY],
    queryFn: async () => {
      return getLsAdminCertificateInfo()
    },
  })

  const mapper = (certificates: CertficateInfo[] = []) => {
    return orderBy(certificates || [], [
      'market',
      'brand',
      'zone',
      'locationName',
      'location',
    ]).map((item) => ({
      market: item.market,
      brand: item.brand,
      zone: item.zone,
      locationName: item.locationName,
      location: item.location,
      notAfter: DateTime.fromJSDate(new Date(item.notAfter))
        .setZone(timezone)
        .toISO(),
      expired: (
        <StyledBadgeDiv>
          <IWGeneralBadge
            label={t(
              `loadScheduling.labels.${
                item.expired
                  ? 'expired'
                  : item.expiringWithinAMonth
                  ? 'expiringSoon'
                  : 'active'
              }`,
            )}
            color={
              item.expired
                ? 'alert'
                : item.expiringWithinAMonth
                ? 'warning'
                : 'primary'
            }
          />
        </StyledBadgeDiv>
      ),
    }))
  }

  useEffect(() => {
    if (certs?.length) {
      setItems(mapper(certs))
    }
  }, [certs])

  return (
    <main>
      <LsAdminPageSectionHeader
        header={t('loadScheduling.titles.adminCertsInfo')}
        desc={t('loadScheduling.descriptions.adminCertsInfo')}
      >
        {isLoading && <IWLoading />}

        {Boolean(items?.length) && (
          <IWGenericTable
            border
            isLoading={isLoading}
            items={items}
            pinnedColumns={{ expired: 'right' }}
            columns={tableColumns as GenericTableColumn[]}
            showColumnSettings
            showEmptyMessage={!isLoading && !Boolean(items.length)}
          />
        )}
      </LsAdminPageSectionHeader>
    </main>
  )
}
